import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

// Note: firebase.config.js currently initializes the app; ensure getStorage uses same app
const storage = getStorage();

function sanitizeFilename(name) {
    return name.replace(/[^a-zA-Z0-9_.-]/g, '_');
}

// Upload multiple File objects under a scoped path and return metadata array
// scopeType: 'conversation' | 'broadcast'
export async function uploadFiles({ userId, scopeType, scopeId, messageId, files = [], onProgress = null }) {
    if (!userId) throw new Error('userId required for upload');
    if (!scopeId) throw new Error('scopeId required for upload');
    if (!messageId) throw new Error('messageId required for upload');

    const uploaded = [];

    for (const file of files) {
        const fileId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const filename = `${fileId}-${sanitizeFilename(file.name)}`;
        const storagePath = `attachments/${userId}/${scopeType}/${scopeId}/${messageId}/${filename}`;
        const storageRef = ref(storage, storagePath);

        // upload
        await new Promise((resolve, reject) => {
            const uploadTask = uploadBytesResumable(storageRef, file);
            uploadTask.on('state_changed', (snapshot) => {
                if (onProgress && snapshot.totalBytes) {
                    onProgress({ loaded: snapshot.bytesTransferred, total: snapshot.totalBytes });
                }
            }, (err) => reject(err), async () => {
                // finished
                uploaded.push({
                    attachmentId: fileId,
                    storagePath,
                    filename: file.name,
                    mimeType: file.type || 'application/octet-stream',
                    size: file.size,
                    source: 'outbound'
                });
                resolve();
            });
        });
    }

    return uploaded;
}

// Generate signed URL for secure attachment access
// Expires in 1 hour by default
export async function getAttachmentSignedUrl(storagePath, expiresInMs = 60 * 60 * 1000) {
    if (!storagePath) throw new Error('storagePath required');

    try {
        const storageRef = ref(storage, storagePath);
        const url = await getDownloadURL(storageRef);
        // Note: Firebase Storage getDownloadURL returns a signed URL if the file is not public
        // If files are set to private, this will fail unless proper auth is configured
        return url;
    } catch (error) {
        console.error('Error getting signed URL for attachment:', storagePath, error);
        throw error;
    }
}

export default { uploadFiles, getAttachmentSignedUrl };
